import type { ReactNode, RefObject } from 'react'
import type { SetupRow, SetupStepId } from '@shared/setup'
import { LockIcon } from '../../security/components/lock-icon'
import { PERMISSION_CONTENT } from '../data/permission-content'
import { blockedLine, notificationSettingsSteps, TURNED_ON, type StepCopy } from '../data/setup-copy'

const PERMISSION_KIND: Partial<Record<SetupStepId, keyof typeof PERMISSION_CONTENT>> = {
  notifications: 'notifications',
  microphone: 'microphone',
  camera: 'camera',
  location: 'location',
  'location-precise': 'location',
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M11 18.5h2" strokeLinecap="round" />
    </svg>
  )
}

function BatteryIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <rect x="3" y="7" width="16" height="10" rx="2" />
      <path d="M21 10.5v3" strokeLinecap="round" />
      <path d="M11.5 9l-2 3.5h3l-2 3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
    </svg>
  )
}

export function stepIcon(id: SetupStepId): ReactNode {
  const kind = PERMISSION_KIND[id]
  if (kind) return PERMISSION_CONTENT[kind].icon
  if (id === 'battery') return <BatteryIcon />
  if (id === 'app-lock') return <LockIcon className="h-6 w-6" />
  if (id === 'session-recording-info') return <InfoIcon />
  return <PhoneIcon />
}

function StepNumber({ n }: { n: number }) {
  return (
    <span
      aria-hidden
      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700"
    >
      {n}
    </span>
  )
}

function NumberedSteps({ steps, label }: { steps: readonly string[]; label: string }) {
  return (
    <ol className="mt-3 space-y-2 rounded-xl bg-slate-50 px-3.5 py-3" aria-label={label}>
      {steps.map((step, i) => (
        <li key={step} className="flex items-start gap-2 text-[15px] text-slate-700">
          <StepNumber n={i + 1} />
          <span>{step}</span>
        </li>
      ))}
    </ol>
  )
}

export type SetupStepCardProps = {
  id: SetupStepId
  copy: StepCopy
  row: SetupRow
  platform: 'ios' | 'android' | 'web'
  /** Native shells can deep-link to Settings; browsers can't. */
  canOpenSettings: boolean
  busy: boolean
  error: string | null
  /** Plain feedback after a tap that did not turn the step on (not an error). */
  notice?: string | null
  headingRef: RefObject<HTMLHeadingElement | null>
  headingId: string
  onPrimary: () => void
  /** Blocked card: "I've done it" re-checks right away. */
  onCheckAgain: () => void
  onLater: () => void
  /** Revisiting a done step (manual replay): a single Next. */
  onNext: () => void
}

const primaryClass =
  'min-h-12 w-full rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-3 text-base font-semibold text-white shadow-md shadow-brand-600/25 outline-none transition hover:from-brand-600 hover:to-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'
const secondaryClass =
  'min-h-12 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-base font-semibold text-slate-900 outline-none transition hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60'
const laterClass =
  'min-h-11 w-full rounded-xl px-4 py-2.5 text-base font-medium text-slate-500 outline-none transition hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-brand-500'

/** One step: what it's for, what happens if you say no, and one action. */
export function SetupStepCard({
  id,
  copy,
  row,
  platform,
  canOpenSettings,
  busy,
  error,
  notice = null,
  headingRef,
  headingId,
  onPrimary,
  onCheckAgain,
  onLater,
  onNext,
}: SetupStepCardProps) {
  const kind = PERMISSION_KIND[id]
  const blocked = row.state === 'blocked'
  const done = row.state === 'done'
  const info = row.level === 'info'

  let blockedSteps: string[] | null = null
  let webSteps: { chrome: string[]; safari: string[] } | null = null
  if (blocked && id !== 'location-precise' && kind) {
    if (platform === 'web') webSteps = PERMISSION_CONTENT[kind].webDeniedSteps
    else if (id === 'notifications') blockedSteps = notificationSettingsSteps(platform)
    else blockedSteps = ['Tap Open Settings', ...PERMISSION_CONTENT[kind].settingsSteps[platform], 'Come back here']
  }

  return (
    <div>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
        {stepIcon(id)}
      </div>
      <h2
        id={headingId}
        ref={headingRef}
        tabIndex={-1}
        className="mt-3 text-xl font-bold tracking-tight text-slate-900 outline-none"
      >
        {copy.title}
      </h2>

      {done && !info ? (
        <p className="mt-2 text-[17px] font-medium text-emerald-700" role="status">
          {TURNED_ON}
        </p>
      ) : blocked && (blockedSteps || webSteps) ? (
        <>
          <p className="mt-2 text-[17px] leading-relaxed text-slate-700">{blockedLine(platform)}</p>
          {blockedSteps && <NumberedSteps steps={blockedSteps} label={`How to turn on ${copy.title.toLowerCase()}`} />}
          {webSteps && (
            <>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Chrome</p>
              <NumberedSteps steps={webSteps.chrome} label="Chrome steps" />
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Safari</p>
              <NumberedSteps steps={webSteps.safari} label="Safari steps" />
            </>
          )}
        </>
      ) : (
        <>
          <ul className="mt-2 space-y-1.5">
            {copy.lines.map((line) => (
              <li key={line} className="text-[17px] leading-relaxed text-slate-700">
                {line}
              </li>
            ))}
          </ul>
          {copy.tip && (
            <p className="mt-3 rounded-xl bg-brand-50 px-3.5 py-2.5 text-[15px] font-medium text-brand-900">{copy.tip}</p>
          )}
          {copy.settingsSteps && <NumberedSteps steps={copy.settingsSteps} label={`Steps for ${copy.title.toLowerCase()}`} />}
          {copy.ifNo && (
            <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-600">If you say no: </span>
              {copy.ifNo}
            </p>
          )}
        </>
      )}

      {/* Always mounted so screen readers announce the text when it fills in. */}
      <div role="status" aria-live="polite">
        {notice && !done && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-[15px] font-medium text-amber-900">
            {notice}
          </p>
        )}
      </div>

      {error && (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <div className="mt-5 flex flex-col gap-2">
        {done && !info ? (
          <button type="button" onClick={onNext} className={primaryClass}>
            Next
          </button>
        ) : blocked && (blockedSteps || webSteps || copy.settingsSteps) ? (
          <>
            {canOpenSettings && (
              <button type="button" onClick={onPrimary} disabled={busy} className={primaryClass}>
                {busy ? 'Opening…' : 'Open Settings'}
              </button>
            )}
            <button type="button" onClick={onCheckAgain} disabled={busy} className={secondaryClass}>
              I’ve done it
            </button>
            <button type="button" onClick={onLater} className={laterClass}>
              {copy.later ?? 'Not now'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onPrimary} disabled={busy} className={primaryClass}>
              {busy ? 'Waiting…' : copy.primary}
            </button>
            {copy.later && (
              <button type="button" onClick={onLater} className={laterClass}>
                {copy.later}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
